import { Container, Graphics } from 'pixi.js';
import type { World, EntityId } from '@/core/ECS';
import { Absorbable, Drift, Position, Renderable, Velocity } from '@/components';
import type { FoodDef } from '@/data/types';
import { rand } from '@/utils/rng';
import { drawProtein, drawSugar, drawBacteria, drawDNA } from '@/visuals/bio';

/**
 * createFood — generic food entity factory. Visuals are dispatched per
 * foodId to a biology-themed draw routine in /visuals/bio. Drift applies
 * Brownian-ish motion; 'swim' types (bacteria) move faster with a
 * wagging flagellum.
 */
export function createFood(
  world: World,
  parent: Container,
  def: FoodDef,
  x: number,
  y: number,
  options?: { bloomId?: number; driftScale?: number },
): EntityId {
  const e = world.createEntity();

  const root = new Container();
  root.position.set(x, y);

  const glow = new Graphics();
  const body = new Graphics();
  root.addChild(glow, body);
  parent.addChild(root);

  const draw = pickFoodDraw(def);
  // initial draw
  draw(rand() * 10, body);

  world.addComponent(e, Position, { x, y });
  world.addComponent(e, Velocity, { vx: 0, vy: 0 });
  world.addComponent(e, Renderable, {
    root, body, glow, baseScale: 1, time: rand() * 10,
    draw: (t, b) => draw(t, b),
  });
  world.addComponent(e, Absorbable, {
    foodId: def.id,
    biomass: def.biomass,
    dna: def.dna,
    radius: def.radius,
    codexId: def.codexId,
    bloomId: options?.bloomId,
  });

  const angle = rand() * Math.PI * 2;
  const rawSpeed = def.behavior === 'swim' ? (def.speed ?? 40) : 8 + rand() * 8;
  const rawStrength = def.behavior === 'swim' ? 40 : 12;
  const ds = options?.driftScale ?? 1;
  world.addComponent(e, Drift, {
    baseVx: Math.cos(angle) * rawSpeed * ds,
    baseVy: Math.sin(angle) * rawSpeed * ds,
    wanderSeed: rand() * 1000,
    strength: rawStrength * ds,
  });

  return e;
}

function pickFoodDraw(def: FoodDef): (time: number, body: Graphics) => void {
  switch (def.id) {
    case 'protein':
      return (t, b) => drawProtein(b, t);
    case 'sugar':
      // static glucose ring — redrawing each frame is fine but unnecessary;
      // we still return a draw fn so the pipeline is uniform
      return (_t, b) => drawSugar(b);
    case 'bacteria':
      return (t, b) => drawBacteria(b, t);
    case 'dna':
      return (t, b) => drawDNA(b, t);
    default:
      return (_t, b) => drawGenericDot(b, def);
  }
}

function drawGenericDot(body: Graphics, def: FoodDef): void {
  body.clear();
  body.circle(0, 0, def.radius * 1.15).fill({ color: def.color, alpha: 0.35 });
  body.circle(0, 0, def.radius).fill({ color: def.color, alpha: 0.85 });
  body.circle(0, 0, def.radius * 0.4).fill({ color: 0xffffff, alpha: 0.5 });
}
